/* eslint-disable react/display-name */
import React from 'react';
import CreatableSelect from 'react-select/creatable';
import styled, { useTheme } from 'styled-components';
import { spacing } from 'ui/theme/utils';

export const MultiTextInput = React.forwardRef(
  (
    {
      value: initialValue = [],
      onChange,
      minWidth,
      minHeight,
      color,
      bold,
      placeholderOpacity,
      required,
      disabled,
      validateValues = () => true,
      ...rest
    },
    ref
  ) => {
    const [value, setValue] = React.useState([]);
    const [inputValue, setInputValue] = React.useState('');
    const theme = useTheme();

    React.useLayoutEffect(() => {
      if (!initialValue) return;
      setValue(initialValue.map((value) => createOption(value)));
    }, [initialValue]);

    const handleChange = (value) => {
      if (onChange) {
        onChange(value.map((option) => option.value));
      } else {
        setValue(value);
      }
    };

    const handleInputChange = (value, { action }) => {
      switch (action) {
        case 'input-change':
          setInputValue(value);
          break;
      }
    };

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Enter':
        case 'Tab':
          addValue();
          event.preventDefault();
          break;
      }
    };

    const addValue = () => {
      if (!inputValue) return;

      if (validateValues(inputValue)) {
        setInputValue('');
        handleChange([...value, createOption(inputValue)]);
      }
    };

    return (
      <>
        <StyledSelect
          {...rest}
          ref={ref}
          styles={customStyles({
            theme,
            minWidth,
            minHeight,
            color,
            bold,
            placeholderOpacity,
          })}
          components={{
            DropdownIndicator: null,
          }}
          inputValue={inputValue}
          isMulti
          disabled={disabled}
          menuIsOpen={false}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onMenuClose={addValue}
          value={value}
        />
        {required && !disabled && <HiddenInput value={value} required />}
      </>
    );
  }
);

const createOption = (label) => ({
  label,
  value: label,
});

const customStyles = ({ theme, minWidth, minHeight, color, bold, placeholderOpacity }) => {
  const styles = {
    value: bold
      ? {
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '0.04em',
        }
      : {},
    option: bold
      ? {
          fontWeight: 400,
          fontSize: '14px',
          letterSpacing: '0.04em',
        }
      : {},
  };

  return {
    control: (provided) => ({
      ...provided,
      border: 0,
      boxShadow: 'none',
      borderRadius: 'inherit',
      backgroundColor: 'transparent',
      minWidth: minWidth || 'inherit',
      minHeight: minHeight || provided.minHeight,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: color || provided.color,
      opacity: placeholderOpacity || 1,
      ...styles.value,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: color || theme.colors.grey,
      ...styles.value,
    }),
    multiValue: (provided) => ({
      ...provided,
      cursor: 'default',
      '> div': {
        fontSize: '13px',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      cursor: 'text',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#ccc',
      ':hover': {
        color: '#f74b4b',
      },
      '> svg': {
        width: '18px',
        height: '18px',
      },
    }),
  };
};

const HiddenInput = styled.input.attrs(() => ({
  tabIndex: -1,
  autoComplete: 'off',
  onChange: () => {},
}))`
  opacity: 0;
  width: 100%;
  height: 0;
  position: absolute;
  bottom: 0;
  z-index: -1;
`;

const StyledSelect = styled(CreatableSelect)`
  background-color: ${({ bg = 'white', theme }) => theme.colors[bg] || bg};
  color: ${({ theme }) => theme.colors.grey};
  box-shadow: ${({ boxShadow = 'bottom', variant, theme }) => {
    switch (variant) {
      case 'minimal':
        return theme.shadows.round;
      default:
        return theme.shadows[boxShadow] || boxShadow;
    }
  }};
  padding: 0;
  border-radius: ${({ borderRadius = '8px' }) => borderRadius};
  border: none;
  width: 100%;
  max-width: ${({ maxWidth = 'initial' }) => maxWidth};
  text-align: left;
  letter-spacing: 0.62px;
  font-weight: 400;
  font-size: 13px;
  ${(props) => spacing(props)}
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}
`;
